//src/pages/DashboardPage.tsx
import { useEffect, useMemo, useState } from "react";
import {
  Activity,
  AlertTriangle,
  CheckCircle2,
  ClipboardList,
  Clock3,
  FileWarning,
  Filter,
  Megaphone,
  ShieldAlert,
  ShieldCheck,
  Siren,
  UserRoundX,
} from "lucide-react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { PageHeader } from "@/components/shared/PageHeader";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { useDashboardOverview } from "@/hooks/useDashboard";
import type { DashboardRiskMatrix, DashboardRiskMatrixCell } from "@/types/dashboard";
import { useRiskInventories } from "@/hooks/useRiskInventories";

const CHART_COLORS = [
  "hsl(var(--primary))",
  "hsl(var(--chart-2))",
  "hsl(var(--chart-3))",
  "hsl(var(--chart-4))",
  "hsl(var(--chart-5))",
  "hsl(0 84% 60%)",
  "hsl(38 92% 50%)",
  "hsl(142 71% 45%)",
];

function parseServerDate(value?: string | null) {
  if (!value) return null;
  const raw = String(value);
  const hasTimezone = /[zZ]|[+-]\d{2}:?\d{2}$/.test(raw);
  return new Date(hasTimezone ? raw : `${raw}Z`);
}

function formatDateTimeBR(value?: string | null) {
  const parsed = parseServerDate(value);
  if (!parsed || Number.isNaN(parsed.getTime())) return "-";
  return new Intl.DateTimeFormat("pt-BR", {
    dateStyle: "short",
    timeStyle: "short",
  }).format(parsed);
}

function formatDateBR(value?: string | null) {
  const parsed = parseServerDate(value);
  if (!parsed || Number.isNaN(parsed.getTime())) return "-";
  return new Intl.DateTimeFormat("pt-BR").format(parsed);
}

function actionStatusLabel(status: string) {
  if (status === "completed") return "Concluída";
  if (status === "cancelled") return "Cancelada";
  if (status === "in_progress") return "Em andamento";
  return "Não iniciada";
}

function campaignStatusLabel(status: string) {
  if (status === "published") return "Publicada";
  if (status === "closed") return "Encerrada";
  if (status === "draft") return "Rascunho";
  return status;
}

function riskLevelBadgeClass(code?: string | null) {
  if (code === "critical") return "border-red-200 bg-red-50 text-red-700";
  if (code === "high") return "border-amber-200 bg-amber-50 text-amber-700";
  if (code === "moderate") return "border-slate-200 bg-slate-100 text-slate-700";
  if (code === "low") return "border-emerald-200 bg-emerald-50 text-emerald-700";
  return "border-slate-200 bg-slate-50 text-slate-700";
}

function getStatusColor(status: string) {
  const normalized = (status || "").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");
  if (normalized.includes("concluid") || normalized === "completed" || normalized === "closed" || normalized.includes("encerrad")) return "hsl(142 71% 45%)";
  if (normalized.includes("cancelad") || normalized === "cancelled") return "hsl(0 84% 60%)";
  if (normalized.includes("andamento") || normalized === "in_progress" || normalized === "published" || normalized.includes("publicad")) return "hsl(217 91% 60%)";
  if (normalized.includes("nao iniciad") || normalized === "not_started") return "hsl(262 83% 58%)";
  if (normalized.includes("rascunho") || normalized === "draft") return "hsl(38 92% 50%)";
  return "hsl(220 14% 60%)";
}

function getPriorityColor(priority: string) {
  if (priority === "Crítica" || priority === "critical") return "hsl(0 84% 60%)";
  if (priority === "Alta" || priority === "high") return "hsl(38 92% 50%)";
  if (priority === "Baixa" || priority === "low") return "hsl(142 71% 45%)";
  return "hsl(var(--primary))";
}

function colorByIndex(index: number) {
  return CHART_COLORS[index % CHART_COLORS.length];
}

function getBarLabelFill(value: number) {
  return value <= 0 ? "hsl(var(--foreground))" : "#fff";
}


function formatTooltipNumber(value: unknown) {
  const num = Number(value || 0);
  return Number.isFinite(num) ? String(num) : "0";
}

function DashboardChartTooltip({ active, payload, label }: { active?: boolean; payload?: Array<{ name?: string; value?: unknown; color?: string; payload?: Record<string, unknown> }>; label?: string; }) {
  if (!active || !payload?.length) return null;

  return (
    <div className="rounded-lg border border-border/70 bg-background px-3 py-2 text-xs shadow-md">
      {label ? <div className="mb-2 font-medium text-foreground">{label}</div> : null}
      <div className="space-y-1.5">
        {payload.map((entry, index) => {
          const originalValue = entry.name === "Público-alvo"
            ? ((entry.payload?.target_audience_original as unknown) ?? entry.value ?? 0)
            : entry.name === "Respondentes"
              ? ((entry.payload?.respondents_original as unknown) ?? entry.value ?? 0)
              : ((entry.payload?.original_value as unknown) ?? entry.value ?? 0);
          return (
            <div key={`${entry.name}-${index}`} className="flex items-center gap-2 text-muted-foreground">
              <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: entry.color || "hsl(var(--primary))" }} />
              <span className="font-medium text-foreground">{entry.name || "Quantidade"}:</span>
              <span>{formatTooltipNumber(originalValue)}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function StatCard({
  title,
  value,
  helper,
  icon,
}: {
  title: string;
  value: string | number;
  helper?: string;
  icon: React.ReactNode;
}) {
  return (
    <Card className="border-border/70 shadow-sm">
      <CardContent className="flex items-start justify-between gap-3 p-4">
        <div>
          <div className="text-xs font-medium uppercase tracking-[0.14em] text-muted-foreground">{title}</div>
          <div className="mt-2 text-2xl font-bold tracking-tight">{value}</div>
          {helper ? <div className="mt-1 text-xs text-muted-foreground">{helper}</div> : null}
        </div>
        <div className="rounded-xl border border-border/60 bg-muted/40 p-2.5 text-muted-foreground">{icon}</div>
      </CardContent>
    </Card>
  );
}

function DashboardSkeleton() {
  return (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-4 xl:grid-cols-8">
        {Array.from({ length: 8 }).map((_, index) => (
          <Card key={index}>
            <CardContent className="p-4">
              <Skeleton className="h-3 w-24" />
              <Skeleton className="mt-3 h-8 w-16" />
              <Skeleton className="mt-2 h-3 w-28" />
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid gap-4 xl:grid-cols-12">
        <Card className="xl:col-span-7">
          <CardHeader><Skeleton className="h-5 w-40" /></CardHeader>
          <CardContent><Skeleton className="h-72 w-full" /></CardContent>
        </Card>
        <Card className="xl:col-span-5">
          <CardHeader><Skeleton className="h-5 w-36" /></CardHeader>
          <CardContent><Skeleton className="h-72 w-full" /></CardContent>
        </Card>
      </div>

      <div className="grid gap-4 xl:grid-cols-3">
        {Array.from({ length: 3 }).map((_, index) => (
          <Card key={index}>
            <CardHeader><Skeleton className="h-5 w-44" /></CardHeader>
            <CardContent className="space-y-3">
              {Array.from({ length: 5 }).map((__, row) => (
                <Skeleton key={row} className="h-14 w-full" />
              ))}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

function LegendTags({ items }: { items: { label: string; color: string }[] }) {
  return (
    <div className="mt-3 flex flex-wrap gap-2">
      {items.map((item) => (
        <div
          key={item.label}
          className="inline-flex items-center gap-2 rounded-full border border-border/70 px-2.5 py-1 text-xs whitespace-nowrap"
        >
          <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: item.color }} />
          {item.label}
        </div>
      ))}
    </div>
  );
}

function minVisibleBar(value: number) {
  return value === 0 ? 0.6 : value;
}

function matrixFallbackColor(code?: string | null) {
  if (code === "critical") return "#FEE2E2";
  if (code === "high") return "#FEF3C7";
  if (code === "moderate") return "#E5E7EB";
  if (code === "low") return "#DCFCE7";
  return "#F8FAFC";
}

function getMatrixCellColor(cell: DashboardRiskMatrixCell) {
  return cell.color_hex || matrixFallbackColor(cell.risk_level_code);
}

function uniqueSorted<T>(items: T[], key: (item: T) => number, direction: "asc" | "desc") {
  const mapped = new Map<number, T>();
  items.forEach((item) => {
    mapped.set(key(item), item);
  });
  return Array.from(mapped.values()).sort((a, b) => {
    const result = key(a) - key(b);
    return direction === "asc" ? result : -result;
  });
}

function RiskMatrixCard({ matrix }: { matrix?: DashboardRiskMatrix | null }) {
  const cells = matrix?.cells || [];
  const probabilityLevels = uniqueSorted(cells, (cell) => cell.probability_order, "desc");
  const severityLevels = uniqueSorted(cells, (cell) => cell.severity_order, "asc");
  const cellByAxis = useMemo(() => {
    const map = new Map<string, DashboardRiskMatrixCell>();
    cells.forEach((cell) => {
      map.set(`${cell.probability_order}-${cell.severity_order}`, cell);
    });
    return map;
  }, [cells]);

  return (
    <Card className="border-border/70 shadow-sm xl:col-span-12">
      <CardHeader className="space-y-2">
        <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
          <div>
            <CardTitle className="text-base">Matriz de riscos</CardTitle>
            <p className="mt-1 text-sm text-muted-foreground">
              Distribuição dos riscos do inventário por probabilidade e severidade.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Badge variant="secondary">{matrix?.total_classified || 0} riscos classificados</Badge>
            {matrix?.pending_classification ? (
              <Badge variant="outline" className="border-amber-200 bg-amber-50 text-amber-700">
                {matrix.pending_classification} pendentes
              </Badge>
            ) : null}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {!matrix?.inventory_id ? (
          <div className="rounded-xl border border-dashed border-border/80 bg-muted/20 p-6 text-sm text-muted-foreground">
            Selecione um inventário para visualizar a matriz de riscos.
          </div>
        ) : cells.length === 0 ? (
          <div className="rounded-xl border border-dashed border-border/80 bg-muted/20 p-6 text-sm text-muted-foreground">
            Este inventário ainda não possui matriz configurada ou células cadastradas.
          </div>
        ) : (
          <div className="overflow-x-auto pb-1">
            <div className="min-w-[760px]">
              <div className="mb-2 grid grid-cols-[140px_repeat(5,minmax(108px,1fr))] gap-2">
                <div className="flex items-end justify-center rounded-xl border border-border/70 bg-muted/30 px-3 py-2 text-center text-xs font-medium text-muted-foreground">
                  Probabilidade \ Severidade
                </div>
                {severityLevels.map((severity) => (
                  <div key={severity.severity_level_id} className="rounded-xl border border-border/70 bg-muted/30 px-3 py-2 text-center">
                    <div className="text-xs font-semibold text-foreground">{severity.severity_name}</div>
                    <div className="text-[11px] text-muted-foreground">{severity.severity_score}</div>
                  </div>
                ))}
              </div>

              <div className="grid gap-2">
                {probabilityLevels.map((probability) => (
                  <div key={probability.probability_level_id} className="grid grid-cols-[140px_repeat(5,minmax(108px,1fr))] gap-2">
                    <div className="flex flex-col justify-center rounded-xl border border-border/70 bg-muted/30 px-3 py-2 text-center">
                      <div className="text-xs font-semibold text-foreground">{probability.probability_name}</div>
                      <div className="text-[11px] text-muted-foreground">{probability.probability_score}</div>
                    </div>
                    {severityLevels.map((severity) => {
                      const cell = cellByAxis.get(`${probability.probability_order}-${severity.severity_order}`);
                      const riskCount = cell?.risks_count || 0;
                      return (
                        <div
                          key={`${probability.probability_level_id}-${severity.severity_level_id}`}
                          className="min-h-[86px] rounded-2xl border border-border/70 p-3 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
                          style={{ backgroundColor: cell ? getMatrixCellColor(cell) : "#F8FAFC" }}
                          title={cell?.risk_level_name || "Sem classificação"}
                        >
                          <div className="flex h-full flex-col items-center justify-center text-center">
                            <div className="text-xl font-bold tracking-tight text-slate-900">
                              {riskCount} {riskCount === 1 ? "risco" : "riscos"}
                            </div>
                            <div className="mt-1 line-clamp-1 text-[11px] text-slate-600">{cell?.risk_level_name || "-"}</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function DashboardPage() {
  const inventoriesQ = useRiskInventories({});
  const inventories = inventoriesQ.data || [];
  const [inventoryId, setInventoryId] = useState<string>("");
  const [campaignFilter, setCampaignFilter] = useState<string>("all");

  const defaultInventoryId = useMemo(() => {
    const activeInventory = inventories.find((inventory) => inventory.status === "active");
    return activeInventory?.id || inventories[0]?.id || "";
  }, [inventories]);

  useEffect(() => {
    if (!inventoryId && defaultInventoryId) {
      setInventoryId(defaultInventoryId);
    }
  }, [defaultInventoryId, inventoryId]);

  const overviewQ = useDashboardOverview(inventoryId || undefined);

  const data = overviewQ.data;

  const generatedAtLabel = useMemo(
    () => (data?.generated_at ? formatDateTimeBR(data.generated_at) : "-"),
    [data?.generated_at],
  );

  const campaignItems = data?.campaigns.progress_items || [];
  const filteredCampaignItems = useMemo(() => {
    if (campaignFilter === "all") return campaignItems;
    return campaignItems.filter((item) => item.id === campaignFilter);
  }, [campaignItems, campaignFilter]);

  const filteredCampaignStats = useMemo(() => {
    if (!data) return null;
    if (campaignFilter === "all") return data.campaigns;
    const selected = campaignItems.find((item) => item.id === campaignFilter);
    if (!selected) return data.campaigns;
    return {
      ...data.campaigns,
      in_progress: selected.status === "published" ? 1 : 0,
      closed: selected.status === "closed" ? 1 : 0,
      target_audience: selected.participants_count,
      respondents: selected.responded_count,
      response_percentage: selected.progress_percentage,
      by_status: data.campaigns.by_status,
      progress_items: filteredCampaignItems,
    };
  }, [campaignFilter, campaignItems, data, filteredCampaignItems]);

  const actionStatusData = (data?.actions.by_status || []).map((item) => ({ ...item, value: minVisibleBar(item.value), original_value: item.value, color: getStatusColor(item.key || item.label) }));
  const actionPriorityData = (data?.actions.by_priority || []).map((item) => ({ ...item, value: minVisibleBar(item.value), original_value: item.value, color: getPriorityColor(item.key || item.label) }));
  const riskLevelData = (data?.risks.by_risk_level || []).map((item, index) => ({ ...item, value: minVisibleBar(item.value), original_value: item.value, color: colorByIndex(index) }));
  const riskDimensionData = (data?.risks.by_dimension || []).map((item, index) => ({ ...item, value: minVisibleBar(item.value), original_value: item.value, color: colorByIndex(index) }));
  const backlogData = (data?.governance.top_responsibles_backlog || []).map((item, index) => ({ ...item, actions_count: minVisibleBar(item.actions_count), original_value: item.actions_count, color: colorByIndex(index) }));
  const campaignStatusData = (data?.campaigns.by_status || []).map((item) => ({ ...item, value: minVisibleBar(item.value), original_value: item.value, color: getStatusColor(item.key || item.label) }));
  const campaignProgressChartData = filteredCampaignItems.map((item, index) => ({
    ...item,
    target_audience_visual: minVisibleBar(item.participants_count),
    respondents_visual: minVisibleBar(item.responded_count),
    target_audience_original: item.participants_count,
    respondents_original: item.responded_count,
    campaign_color: colorByIndex(index),
    target_color: "hsl(220 14% 78%)",
    respondents_color: "hsl(var(--primary))",
  }));

  return (
    <div className="space-y-6">
      <PageHeader title="Dashboard" description="Visão executiva da execução, campanhas, riscos e governança operacional." />

      <Card className="border-border/70 shadow-sm">
        <CardContent className="flex flex-col gap-3 p-4 md:flex-row md:items-end md:justify-between">
          <div className="grid gap-2 md:min-w-[320px]">
            <Label htmlFor="dashboard-inventory-filter" className="text-xs uppercase tracking-[0.14em] text-muted-foreground">
              Inventário
            </Label>
            <Select value={inventoryId || "none"} onValueChange={(value) => value !== "none" && setInventoryId(value)}>
              <SelectTrigger id="dashboard-inventory-filter" className="bg-background">
                <SelectValue placeholder="Selecione um inventário" />
              </SelectTrigger>
              <SelectContent>
                {inventories.length ? inventories.map((inventory) => (
                  <SelectItem key={inventory.id} value={inventory.id}>
                    {inventory.name}
                  </SelectItem>
                )) : <SelectItem value="none" disabled>Nenhum inventário encontrado</SelectItem>}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Filter className="h-4 w-4" />
            Atualizado em {generatedAtLabel}
          </div>
        </CardContent>
      </Card>

      {overviewQ.isLoading ? <DashboardSkeleton /> : null}

      {!overviewQ.isLoading && data && filteredCampaignStats ? (
        <>
          <section className="space-y-4">
            <div className="flex items-center gap-2">
              <ClipboardList className="h-4 w-4 text-primary" />
              <h2 className="text-base font-semibold">Indicadores de ações</h2>
            </div>
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4 2xl:grid-cols-8">
              <StatCard title="Total de ações" value={data.actions.total} helper="Base operacional consolidada" icon={<Activity className="h-4 w-4" />} />
              <StatCard title="Não iniciadas" value={data.actions.not_started} helper="Ainda sem avanço" icon={<Clock3 className="h-4 w-4" />} />
              <StatCard title="Em andamento" value={data.actions.in_progress} helper="Execução ativa" icon={<Activity className="h-4 w-4" />} />
              <StatCard title="Concluídas" value={data.actions.completed} helper={`${data.actions.percent_completed}% concluído`} icon={<CheckCircle2 className="h-4 w-4" />} />
              <StatCard title="Canceladas" value={data.actions.cancelled} helper="Fora do escopo atual" icon={<FileWarning className="h-4 w-4" />} />
              <StatCard title="Vencidas" value={data.actions.overdue} helper={`${data.actions.percent_overdue}% em atraso`} icon={<AlertTriangle className="h-4 w-4" />} />
              <StatCard title="Sem responsável" value={data.actions.unassigned} helper="Exige definição" icon={<UserRoundX className="h-4 w-4" />} />
              <StatCard title="Críticas vencidas" value={data.actions.critical_overdue} helper="Prioridade máxima" icon={<Siren className="h-4 w-4" />} />
            </div>

            <div className="grid gap-4 xl:grid-cols-12">
              <Card className="xl:col-span-4">
                <CardHeader>
                  <CardTitle className="text-base">Ações por status</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-72 cursor-pointer">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={actionStatusData} margin={{ top: 16, right: 8, left: 8, bottom: 8 }}>
                        <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                        <XAxis dataKey="label" hide />
                        <YAxis allowDecimals={false} className="text-xs" tickLine={false} axisLine={false} />
                        <Tooltip content={<DashboardChartTooltip />} cursor={{ fill: "hsl(var(--muted) / 0.25)" }} wrapperStyle={{ cursor: "pointer" }} />
                        <Bar dataKey="value" name="Quantidade de ações" radius={[10, 10, 0, 0]} style={{ cursor: "pointer" }}>
                          {actionStatusData.map((entry) => (
                            <Cell key={entry.label} fill={entry.color} />
                          ))}
                          <LabelList dataKey="original_value" position="insideTop" fontSize={12} fontWeight={700} content={({ x, y, width, value }: any) => (
                            <text x={(x || 0) + (width || 0) / 2} y={(y || 0) + 16} textAnchor="middle" fill={getBarLabelFill(Number(value || 0))} fontSize={12} fontWeight={700}>{value}</text>
                          )} />
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  <LegendTags items={actionStatusData.map((item) => ({ label: `${item.label} (${item.original_value})`, color: item.color }))} />
                </CardContent>
              </Card>

              <Card className="xl:col-span-4">
                <CardHeader>
                  <CardTitle className="text-base">Ações por prioridade</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-72 cursor-pointer">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={actionPriorityData} margin={{ top: 16, right: 8, left: 8, bottom: 8 }}>
                        <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                        <XAxis dataKey="label" className="text-xs" tickLine={false} axisLine={false} />
                        <YAxis allowDecimals={false} className="text-xs" tickLine={false} axisLine={false} />
                        <Tooltip content={<DashboardChartTooltip />} cursor={{ fill: "hsl(var(--muted) / 0.25)" }} wrapperStyle={{ cursor: "pointer" }} />
                        <Bar dataKey="value" name="Quantidade de ações" radius={[10, 10, 0, 0]} style={{ cursor: "pointer" }}>
                          {actionPriorityData.map((entry) => (
                            <Cell key={entry.label} fill={entry.color} />
                          ))}
                          <LabelList dataKey="original_value" position="insideTop" fontSize={12} fontWeight={700} content={({ x, y, width, value }: any) => (
                            <text x={(x || 0) + (width || 0) / 2} y={(y || 0) + 16} textAnchor="middle" fill={getBarLabelFill(Number(value || 0))} fontSize={12} fontWeight={700}>{value}</text>
                          )} />
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              <Card className="xl:col-span-4">
                <CardHeader>
                  <CardTitle className="text-base">Últimas ações atualizadas</CardTitle>
                </CardHeader>
                <CardContent className="h-[22.5rem] space-y-3 overflow-y-auto pr-1">
                  {data.actions.recent_updates.length ? data.actions.recent_updates.map((item) => (
                    <div key={item.id} className="rounded-xl border border-border/70 bg-muted/30 p-3">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="truncate text-sm font-medium">{item.title}</div>
                          <div className="truncate text-xs text-muted-foreground">{item.plan_title || "-"} • {item.inventory_item_title || "-"}</div>
                        </div>
                        <Badge
                          variant="outline"
                          className="whitespace-nowrap"
                          style={{ borderColor: getStatusColor(item.status), color: getStatusColor(item.status) }}
                        >
                          {actionStatusLabel(item.status)}
                        </Badge>
                      </div>
                      <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                        <span>Responsável: {item.responsible_user_name || "-"}</span>
                        <span>Prazo: {formatDateBR(item.due_date)}</span>
                        <span>Atualizado: {formatDateTimeBR(item.updated_at)}</span>
                        {item.is_overdue ? <Badge className="border-red-200 bg-red-50 text-red-700 whitespace-nowrap">Vencida</Badge> : null}
                      </div>
                    </div>
                  )) : <div className="text-sm text-muted-foreground">Nenhuma ação encontrada.</div>}
                </CardContent>
              </Card>
            </div>
          </section>

          <section className="space-y-4">
            <div className="flex items-center gap-2">
              <Megaphone className="h-4 w-4 text-primary" />
              <h2 className="text-base font-semibold">Indicadores de campanhas</h2>
            </div>
            <div className="grid gap-4 md:grid-cols-[minmax(240px,320px)_1fr]">
              <Card className="border-border/70 shadow-sm">
                <CardContent className="p-4">
                  <div className="grid gap-2">
                    <Label className="text-xs uppercase tracking-[0.14em] text-muted-foreground">Campanha</Label>
                    <Select value={campaignFilter} onValueChange={setCampaignFilter}>
                      <SelectTrigger className="bg-background">
                        <SelectValue placeholder="Selecione uma campanha" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todas</SelectItem>
                        {campaignItems.map((campaign) => (
                          <SelectItem key={campaign.id} value={campaign.id}>{campaign.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </CardContent>
              </Card>
            </div>
            <div className="grid gap-4 md:grid-cols-5">
              <StatCard title="Em andamento" value={filteredCampaignStats.in_progress} helper="Campanhas publicadas" icon={<Activity className="h-4 w-4" />} />
              <StatCard title="Encerradas" value={filteredCampaignStats.closed} helper="Ciclos concluídos" icon={<CheckCircle2 className="h-4 w-4" />} />
              <StatCard title="Público-alvo" value={filteredCampaignStats.target_audience} helper="Convites válidos" icon={<Activity className="h-4 w-4" />} />
              <StatCard title="Respondentes" value={filteredCampaignStats.respondents} helper="Respostas recebidas" icon={<CheckCircle2 className="h-4 w-4" />} />
              <StatCard title="Percentual" value={`${filteredCampaignStats.response_percentage}%`} helper="Taxa geral de resposta" icon={<Activity className="h-4 w-4" />} />
            </div>

            <div className="grid gap-4 xl:grid-cols-12">
              <Card className="xl:col-span-4">
                <CardHeader><CardTitle className="text-base">Campanhas por status</CardTitle></CardHeader>
                <CardContent>
                  <div className="h-72 cursor-pointer">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Tooltip content={<DashboardChartTooltip />} />
                        <Pie data={campaignStatusData} dataKey="value" nameKey="label" innerRadius={68} outerRadius={110} paddingAngle={2}>
                          {campaignStatusData.map((entry) => (
                            <Cell key={entry.label} fill={entry.color} style={{ cursor: "pointer" }} />
                          ))}
                          <LabelList dataKey="original_value" position="inside" fill="#fff" fontSize={12} fontWeight={700} />
                        </Pie>
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <LegendTags items={campaignStatusData.map((item) => ({ label: `${item.label} (${item.original_value})`, color: item.color }))} />
                </CardContent>
              </Card>

              <Card className="xl:col-span-8">
                <CardHeader><CardTitle className="text-base">Progresso das campanhas</CardTitle></CardHeader>
                <CardContent>
                  <div className="h-72 cursor-pointer">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={campaignProgressChartData} margin={{ top: 16, right: 8, left: 8, bottom: 8 }}>
                        <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                        <XAxis
                          dataKey="name"
                          className="text-xs"
                          tickLine={false}
                          axisLine={false}
                          interval={0}
                          height={46}
                        />
                        <YAxis allowDecimals={false} className="text-xs" tickLine={false} axisLine={false} />
                        <Tooltip content={<DashboardChartTooltip />} cursor={{ fill: "hsl(var(--muted) / 0.25)" }} wrapperStyle={{ cursor: "pointer" }} />
                        <Bar dataKey="target_audience_visual" name="Público-alvo" radius={[8, 8, 0, 0]} style={{ cursor: "pointer" }}>
                          {campaignProgressChartData.map((entry) => (
                            <Cell key={`target-${entry.id}`} fill={entry.target_color} />
                          ))}
                          <LabelList dataKey="target_audience_original" position="insideTop" fontSize={12} fontWeight={700} fill="#374151" />
                        </Bar>
                        <Bar dataKey="respondents_visual" name="Respondentes" radius={[8, 8, 0, 0]} style={{ cursor: "pointer" }}>
                          {campaignProgressChartData.map((entry) => (
                            <Cell key={`resp-${entry.id}`} fill={entry.respondents_color} />
                          ))}
                          <LabelList dataKey="respondents_original" position="insideTop" fontSize={12} fontWeight={700} fill="#fff" />
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  <LegendTags items={[{ label: "Público-alvo", color: "hsl(220 14% 78%)" }, { label: "Respondentes", color: "hsl(var(--primary))" }]} />
                </CardContent>
              </Card>
            </div>
          </section>

          <section className="space-y-4">
            <div className="flex items-center gap-2">
              <ShieldAlert className="h-4 w-4 text-primary" />
              <h2 className="text-base font-semibold">Indicadores de riscos</h2>
            </div>
            <div className="grid gap-4 md:grid-cols-4">
              <StatCard title="Total de riscos" value={data.risks.total} helper="Itens no inventário" icon={<ShieldAlert className="h-4 w-4" />} />
              <StatCard title="Com plano" value={data.risks.with_action_plan} helper="Já possuem plano vinculado" icon={<ShieldCheck className="h-4 w-4" />} />
              <StatCard title="Sem plano" value={data.risks.without_action_plan} helper="Exigem atenção" icon={<FileWarning className="h-4 w-4" />} />
              <StatCard title="Sem avanço" value={data.risks.without_progress} helper="Plano sem execução" icon={<Clock3 className="h-4 w-4" />} />
            </div>

            <div className="grid gap-4 xl:grid-cols-12">
              <RiskMatrixCard matrix={data.risk_matrix} />
            </div>

            <div className="grid gap-4 xl:grid-cols-12">
              <Card className="xl:col-span-4">
                <CardHeader><CardTitle className="text-base">Riscos por classificação</CardTitle></CardHeader>
                <CardContent>
                  <div className="h-72 cursor-pointer">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={riskLevelData} margin={{ top: 16, right: 8, left: 8, bottom: 8 }}>
                        <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                        <XAxis dataKey="label" hide />
                        <YAxis allowDecimals={false} className="text-xs" tickLine={false} axisLine={false} />
                        <Tooltip content={<DashboardChartTooltip />} cursor={{ fill: "hsl(var(--muted) / 0.25)" }} wrapperStyle={{ cursor: "pointer" }} />
                        <Bar dataKey="value" name="Quantidade de riscos" radius={[10, 10, 0, 0]} style={{ cursor: "pointer" }}>
                          {riskLevelData.map((entry) => (
                            <Cell key={entry.label} fill={entry.color} />
                          ))}
                          <LabelList dataKey="original_value" position="insideTop" fontSize={12} fontWeight={700} content={({ x, y, width, value }: any) => (
                            <text x={(x || 0) + (width || 0) / 2} y={(y || 0) + 16} textAnchor="middle" fill={getBarLabelFill(Number(value || 0))} fontSize={12} fontWeight={700}>{value}</text>
                          )} />
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  <LegendTags items={riskLevelData.map((item) => ({ label: `${item.label} (${item.original_value})`, color: item.color }))} />
                </CardContent>
              </Card>

              <Card className="xl:col-span-4">
                <CardHeader><CardTitle className="text-base">Riscos por dimensão</CardTitle></CardHeader>
                <CardContent>
                  <div className="h-72 cursor-pointer">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={riskDimensionData} margin={{ top: 16, right: 8, left: 8, bottom: 8 }}>
                        <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                        <XAxis dataKey="label" hide />
                        <YAxis allowDecimals={false} className="text-xs" tickLine={false} axisLine={false} />
                        <Tooltip content={<DashboardChartTooltip />} cursor={{ fill: "hsl(var(--muted) / 0.25)" }} wrapperStyle={{ cursor: "pointer" }} />
                        <Bar dataKey="value" name="Quantidade de riscos" radius={[10, 10, 0, 0]} style={{ cursor: "pointer" }}>
                          {riskDimensionData.map((entry) => (
                            <Cell key={entry.label} fill={entry.color} />
                          ))}
                          <LabelList dataKey="original_value" position="insideTop" fontSize={12} fontWeight={700} content={({ x, y, width, value }: any) => (
                            <text x={(x || 0) + (width || 0) / 2} y={(y || 0) + 16} textAnchor="middle" fill={getBarLabelFill(Number(value || 0))} fontSize={12} fontWeight={700}>{value}</text>
                          )} />
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  <LegendTags items={riskDimensionData.map((item) => ({ label: `${item.label} (${item.original_value})`, color: item.color }))} />
                </CardContent>
              </Card>

              <Card className="xl:col-span-4">
                <CardHeader><CardTitle className="text-base">Últimos riscos atualizados</CardTitle></CardHeader>
                <CardContent className="h-[22.5rem] space-y-3 overflow-y-auto pr-1">
                  {data.risks.recent_updates.length ? data.risks.recent_updates.map((risk) => (
                    <div key={risk.id} className="rounded-xl border border-border/70 bg-muted/30 p-3">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="truncate text-sm font-medium">{risk.title}</div>
                          <div className="text-xs text-muted-foreground">{risk.dimension_name || "Sem dimensão"}</div>
                        </div>
                        <Badge className={`${riskLevelBadgeClass(risk.risk_level_code)} whitespace-nowrap`}>{risk.risk_level_name || "-"}</Badge>
                      </div>
                      <div className="mt-2 flex flex-wrap gap-2 text-xs text-muted-foreground">
                        <span>Status: {risk.status}</span>
                        <span>Planos: {risk.action_plans_count}</span>
                        <span>Atualizado: {formatDateTimeBR(risk.updated_at)}</span>
                      </div>
                    </div>
                  )) : <div className="text-sm text-muted-foreground">Nenhum risco encontrado.</div>}
                </CardContent>
              </Card>
            </div>
          </section>

          <section className="space-y-4">
            <div className="flex items-center gap-2">
              <Siren className="h-4 w-4 text-primary" />
              <h2 className="text-base font-semibold">Governança operacional</h2>
            </div>
            <div className="grid gap-4 xl:grid-cols-12">
              <Card className="xl:col-span-4">
                <CardHeader><CardTitle className="text-base">Painéis de atenção</CardTitle></CardHeader>
                <CardContent className="h-[22.5rem] space-y-3 overflow-y-auto pr-1">
                  <div className="flex items-center justify-between rounded-xl border border-border/70 px-3 py-3">
                    <span className="text-sm">Ações sem responsável</span>
                    <Badge variant="secondary">{data.governance.unassigned_actions}</Badge>
                  </div>
                  <div className="flex items-center justify-between rounded-xl border border-border/70 px-3 py-3">
                    <span className="text-sm">Ações críticas vencidas</span>
                    <Badge className="border-red-200 bg-red-50 text-red-700">{data.governance.critical_overdue_actions}</Badge>
                  </div>
                  <div className="flex items-center justify-between rounded-xl border border-border/70 px-3 py-3">
                    <span className="text-sm">Riscos sem plano de ação</span>
                    <Badge variant="secondary">{data.governance.risks_without_action_plan}</Badge>
                  </div>
                  <div className="flex items-center justify-between rounded-xl border border-border/70 px-3 py-3">
                    <span className="text-sm">Planos sem avanço</span>
                    <Badge variant="secondary">{data.governance.plans_without_progress}</Badge>
                  </div>
                  <div className="flex items-center justify-between rounded-xl border border-border/70 px-3 py-3">
                    <span className="text-sm">Campanhas com baixa adesão</span>
                    <Badge variant="secondary">{data.governance.low_engagement_campaigns}</Badge>
                  </div>
                </CardContent>
              </Card>

              <Card className="xl:col-span-8">
                <CardHeader><CardTitle className="text-base">Responsáveis com maior backlog</CardTitle></CardHeader>
                <CardContent>
                  <div className="h-72 cursor-pointer">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={backlogData} margin={{ top: 16, right: 8, left: 8, bottom: 8 }}>
                        <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                        <XAxis dataKey="responsible_user_name" hide />
                        <YAxis allowDecimals={false} className="text-xs" tickLine={false} axisLine={false} />
                        <Tooltip content={<DashboardChartTooltip />} cursor={{ fill: "hsl(var(--muted) / 0.25)" }} wrapperStyle={{ cursor: "pointer" }} />
                        <Bar dataKey="actions_count" name="Quantidade de ações" radius={[10, 10, 0, 0]} style={{ cursor: "pointer" }}>
                          {backlogData.map((entry) => (
                            <Cell key={entry.responsible_user_name} fill={entry.color} />
                          ))}
                          <LabelList dataKey="original_value" position="insideTop" fontSize={12} fontWeight={700} content={({ x, y, width, value }: any) => (
                            <text x={(x || 0) + (width || 0) / 2} y={(y || 0) + 16} textAnchor="middle" fill={getBarLabelFill(Number(value || 0))} fontSize={12} fontWeight={700}>{value}</text>
                          )} />
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                  <LegendTags items={backlogData.map((item) => ({ label: `${item.responsible_user_name} (${item.original_value})`, color: item.color }))} />
                </CardContent>
              </Card>
            </div>
          </section>
        </>
      ) : null}
    </div>
  );
}
